import { createContext, useContext, useMemo, useReducer } from "react";

/**
 * Represents the state of an application with configuration for a guided tour.
 *
 * @property {boolean} run - Indicates if the application is in a "running" state.
 * @property {number} stepIndex - The current step index in the guided tour.
 * @property {Array} steps - An array representing the steps in the tour.
 * @property {boolean} tourActive - Indicates whether the guided tour is currently active.
 */
const workshopUserTourState = {
  run: false,
  stepIndex: 0,
  steps: [],
  tourActive: false,
};

/**
 * Applies a partial update to the tour state. Consumers only ever hand over the
 * keys they want to change, so every patch is shallow-merged onto the previous
 * state.
 *
 * @param {Object} state - The current tour state.
 * @param {Object} patch - The keys to overwrite.
 * @return {Object} The merged state.
 */
const mergeTourState = (state, patch) => ({ ...state, ...patch });

/**
 * Context object for managing the state of the workshop user tour.
 * This context provides a shared state and a method to update the state
 * across components related to the workshop user tour feature.
 *
 * The `state` represents the current state of the user tour, initializing
 * with the `workshopUserTourState` object.
 * The `setState` function is provided as a placeholder function and
 * should be overridden with an actual state updating function.
 */
const WorkshopUserTourContext = createContext({
  state: workshopUserTourState,
  setState: () => undefined,
});

/**
 * Provides the context for the Workshop User Tour.
 * It manages the state related to the workshop user tour and supplies
 * the context value to its descendants.
 *
 * @param {Object} props - The properties passed to the provider component.
 * @return {JSX.Element} The Workshop User Tour context provider component.
 */
export function WorkshopUserTourProvider(props) {
  // `useReducer` rather than `useState` so the setter both shallow-merges
  // partial updates and keeps a stable identity across renders — consumers pass
  // it into effect dependency arrays.
  const [state, setState] = useReducer(mergeTourState, workshopUserTourState);

  const value = useMemo(
    () => ({
      state,
      setState,
    }),
    [state],
  );

  return <WorkshopUserTourContext.Provider value={value} {...props} />;
}

/**
 * Custom hook to access the WorkshopUserTourContext.
 * Ensures that the hook is used within a valid context provider.
 *
 * @return {Object} The value provided by the WorkshopUserTourContext.
 * @throws {Error} If the hook is used outside of a valid WorkshopUserTourContext provider.
 */
export function useWorkshopTourContext() {
  const context = useContext(WorkshopUserTourContext);

  if (!context) {
    throw new Error("useAppContext must be used within a AppProvider");
  }

  return context;
}

/**
 * A tour step, as far as consumers of this hook care — see
 * `config/StepsDataForUserTour.jsx` for the full shape Joyride reads.
 *
 * @typedef {{
 *   target: string,
 *   data?: { openSparkMenu?: boolean },
 * }} TourStep
 */

/**
 * The step the tour is currently on, or `undefined` while no tour is running.
 *
 * Lets the UI react to where the tour is — a step can only point at something
 * that is on screen, so anything that has to be opened for a step (see
 * `data.openSparkMenu`) reads it from here.
 *
 * @return {TourStep|undefined} The current step definition.
 */
export function useCurrentTourStep() {
  const {
    state: { tourActive, steps, stepIndex },
  } = useWorkshopTourContext();

  return tourActive ? steps[stepIndex] : undefined;
}
